import { fetchInterestRates } from '@hex/calculators-shared/api';
import { ErrorMessageWithButtons } from '@hex/calculators-shared/components/ErrorMessageWithButtons';
import { LoadingOverlay } from '@hex/calculators-shared/components/LoadingOverlay';
import { MonthlyLoanCostCard } from '@hex/calculators-shared/components/MonthlyLoanCostCard/MonthlyLoanCostCard';
import { MoreInformation } from '@hex/calculators-shared/components/MoreInformation';
import { amortizationRulesData } from '@hex/calculators-shared/components/MoreInformation/amortizationRulesData';
import { howCalculatedData } from '@hex/calculators-shared/components/MoreInformation/howCalculatedData';
import { loanInsuranceData } from '@hex/calculators-shared/components/MoreInformation/LoanInsuranceData';
import { nextStepsData } from '@hex/calculators-shared/components/MoreInformation/nextStepsData';
import { PreliminaryBorrow } from '@hex/calculators-shared/components/PreliminaryBorrow/PreliminaryBorrow';
import { featureFlags } from '@hex/calculators-shared/constants';
import { ResultWhiteBox } from '@hex/calculators-shared/layout';
import { InterestRatesResponse } from '@hex/calculators-shared/types';
import { AffordabilityFormDataType } from '@hex/calculators-shared/types/affordability';
import {  getLTV, getPartnership } from '@hex/calculators-shared/utils';
import { Error } from '@sapphire/icons/react';
import { FeedbackMessage } from '@sapphire-react/core';
import { debounce } from 'lodash';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MIN_LOAN_AMOUNT } from '../../../constants';
import { AffordabilityResponse } from '../../types';
import { Disclaimer } from './Disclaimer';
import { parseFormData } from './utils';

type Props = {
  interestResponseData: InterestRatesResponse| number | null;
  affordabilityResponseData: AffordabilityResponse | null;
  hasError: boolean;
  isLoading: boolean;
  formData?: AffordabilityFormDataType;
  submitRef: RefObject<any>;
  onError: () => void;
};

export function Results(props: Props) {
  const { t } = useTranslation();
  const { interestResponseData, affordabilityResponseData, hasError, isLoading, formData, submitRef, onError } = props;
  const [affordability, setAffordability] = useState<AffordabilityResponse | null>(null);
  const [interestRates, setInterestRates] = useState<any>(null);
  const [calculatedLoanAmount, setCalculatedLoanAmount] = useState<number | undefined>();
  const { secondaryApplicantExist, downpayment } =
    parseFormData(formData);
  const [initialLoanAmount, setInitialLoanAmount] = useState<number>();
  const ref = useRef<HTMLDivElement>(null);

  const rates = featureFlags.use3MonthInterestRate ? interestRates : interestRates?.interestRates;

  // Temporary solution until we have a proper API response. Now we calculate LTV ourself
  const ltv =
    downpayment && affordability
      ? getLTV((downpayment + affordability.loanAmount).toString(), affordability?.loanAmount.toString())
      : undefined;

  useEffect(() => {
    setAffordability(affordabilityResponseData);
    setCalculatedLoanAmount(affordabilityResponseData?.loanAmount);

    if (affordabilityResponseData) {
      // scroll(ref.current);
      ref.current?.scrollIntoView();
    }

    // MonthlyLoanCostCard needs to be in active state only when loanAmount is more than 0
    if (affordabilityResponseData?.loanAmount && affordabilityResponseData?.loanAmount > 0) {
      setInterestRates(interestResponseData);
      setInitialLoanAmount(affordabilityResponseData?.loanAmount);
    } else {
      setInterestRates(null);
    }
  }, [interestResponseData, affordabilityResponseData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchInterestRatesDebounce = useCallback(
    debounce(async (loanAmount: number) => {
      if (formData) {
        try {
          const responseData = await fetchInterestRates({
            loanAmount: loanAmount.toString(),
            propertyValue: (loanAmount + (downpayment ?? 0)).toString(),
            partnership: getPartnership(formData?.partnership),
          });

          setInterestRates(responseData);
        } catch (_e) {
          onError();
        }
      }
    }, 1500),
    [formData, downpayment],
  );

  const handleLoanAmountChange = (loanAmount: number) => {
    setAffordability((prevVal) => (prevVal ? { ...prevVal, loanAmount } : null));
    setInterestRates(null);
    fetchInterestRatesDebounce(loanAmount);
  };

  if (hasError) {
    return <ErrorMessageWithButtons displayButtons submitRef={submitRef} />;
  }

  if (calculatedLoanAmount != null && calculatedLoanAmount <= MIN_LOAN_AMOUNT) {
    return (
      <ResultWhiteBox>
        <FeedbackMessage
          data-test-id={'loan-amount-error-message'}
          maxWidth={324}
          variant={'informative'}
          icon={<Error />}
          heading={t('calculationResult.error.noIndication.heading')}
          body={t('calculationResult.error.noIndication.body')}
        />
      </ResultWhiteBox>
    );
  }

  return (
    <LoadingOverlay isLoading={isLoading}>
      <PreliminaryBorrow
        initialLoanAmount={initialLoanAmount}
        downpayment={downpayment}
        affordability={affordability}
        onLoanAmountChange={handleLoanAmountChange}
        dataTrackingId={'lead:housing:borrow-calculator'}
        calculatedLoanAmount={calculatedLoanAmount}
      />
      <MonthlyLoanCostCard
        rates={rates}
        ltv={ltv}
        loanAmount={affordability?.loanAmount}
        secondaryApplicantExist={secondaryApplicantExist}
        displayNavigateLoanApplicationButton={false}
        dataTrackingId={'lead:housing:borrow-calculator'}
      />
      {interestResponseData && affordabilityResponseData && affordabilityResponseData.loanAmount > 0 && (
        <>
          <MoreInformation items={[howCalculatedData, amortizationRulesData, loanInsuranceData, nextStepsData]} />
          <Disclaimer />
        </>
      )}
    </LoadingOverlay>
  );
}
